import { Injectable } from '@angular/core';
import { IOrganisationsHierarchy } from './organisation-data.service';
import { HttpClient } from '@angular/common/http';
import { CacheEntry, GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { AssetDataPoint, DataPoint } from './datapoints-data.service';
import { DeviationEvent, OfflineEvent, RuleEvent } from './events-data.service';
import { Action } from './actions-data.service';
import { CaseFile } from './cmms.service';

/**
 * IAssetType
 */
export interface IAssetType {
	/** AssetTypeID */
	AssetTypeID: number;
	/** Type */
	Type: string;
}

/**
 * AssetType
 */
export class AssetType implements IAssetType {
	/**
	 * Default Constructor
	 * @param AssetTypeID AssetTypeID 
	 * @param Type Type 
	 */
	constructor(
		public AssetTypeID: number,
		public Type: string
	) { }
}

/**
 * IAssetInformation
 */
export interface IAssetInformation {
	/** AssetInformationID */
	AssetInformationID: number;

	/** AssetInformationGUID */
	AssetInformationGUID: string;

	/** AssetID */
	AssetID: number;

	/** OrganisationUID */
	OrganisationUID: string;

	/** Title */
	Title: string;

	/** IsFile */
	IsFile: boolean;

	/** AddedBy */
	AddedBy: number;

	/** AddedByName */
	AddedByName: string;

	/** AddedAt */
	AddedAt: number;

	/** Content */
	Content?: string;

	/** FileName */
	FileName?: string;
}

/**
 * AssetInformation
 */
export class AssetInformation implements IAssetInformation {
	/**
	 * Default Constructor
	 * @param AssetInformationID AssetInformationID 
	 * @param AssetID AssetID 
	 * @param OrganisationUID OrganisationUID 
	 * @param Title Title 
	 * @param IsFile IsFile 
	 * @param Content Content 
	 * @param FileName FileName  
	 * @param AddedBy AddedBy 
	 * @param AddedByName AddedByName 
	 * @param AddedAt AddedAt 
	 */
	constructor(
		public AssetInformationID: number,
		public AssetInformationGUID: string,
		public AssetID: number,
		public OrganisationUID: string,
		public Title: string,
		public IsFile: boolean,
		public AddedBy: number,
		public AddedByName: string,
		public AddedAt: number,
		public Content?: string,
		public FileName?: string
	) { }
}

/**
 * IAssetHistory
 */
export interface IAssetHistory {
	/** AssetID */
	AssetID: number;
	/** RuleEventHistory */
	RuleEventHistory: RuleEvent[];
	/** SensorEventHistory */
	SensorEventHistory: OfflineEvent[];
	/** ActionHistory */
	ActionHistory: Action[];
	/** DeviationEventHistory */
	DeviationEventHistory: DeviationEvent[];
	/** CMMSCaseHistory */
	CMMSCaseHistory: CaseFile[]
}

/**
* AssetHistory
* implements IAssetHistory
*/
export class AssetHistory implements IAssetHistory {
	/**
	 * Default Constructor
	 * @param AssetID AssetID 
	 * @param RuleEventHistory RuleEventHistory 
	 * @param SensorEventHistory SensorEventHistory 
	 * @param ActionHistory ActionHistory 
	 * @param DeviationEventHistory DeviationEventHistory
	 * @param CMMSCaseHistory CMMSCaseHistory
	 */
	constructor(
		public AssetID: number,
		public RuleEventHistory: RuleEvent[],
		public SensorEventHistory: OfflineEvent[],
		public ActionHistory: Action[],
		public DeviationEventHistory: DeviationEvent[],
		public CMMSCaseHistory: CaseFile[]
	) { }

}

/**
 * IWatchedAssetData
 */
export interface IWatchedAssetData {
	/** AssetID */
	AssetID: number;
	/** Watched */
	Watched: boolean;
	/** WatchListAssetID */
	WatchListAssetID?: number;
	/** TriggeredRules */
	TriggeredRules?: string;
	/** TriggeredDataPoints */
	TriggeredDataPoints?: string;
	/** TriggeredDeviations */
	TriggeredDeviations?: string;
	/** TimeStamp */
	TimeStamp?: number;
	/** AssetName */
	AssetName?: string;
	/** AssetDescription */
	AssetDescription?: string;
	/** RelatedComments */
	RelatedComments?: string;
}

/**
* WatchedAssetData
* implements IWatchedAssetData
*/
export class WatchedAssetData implements IWatchedAssetData {
	/**
	 * Default Constructor
	 * @param AssetID AssetID 
	 * @param Watched Watched 
	 * @param WatchListAssetID WatchListAssetID  
	 * @param TriggeredRules TriggeredRules 
	 * @param TimeStamp TimeStamp 
	 * @param TriggeredDataPoints TriggeredDataPoints 
	 * @param AssetName AssetName 
	 * @param AssetDescription AssetDescription 
	 * @param RelatedComments RelatedComments 
	 * @param TriggeredDeviations TriggeredDeviations 
	 */
	constructor(
		public AssetID: number,
		public Watched: boolean,
		public WatchListAssetID?: number,
		public TriggeredRules?: string,
		public TimeStamp?: number,
		public TriggeredDataPoints?: string,
		public TriggeredDeviations?: string,
		public AssetName?: string,
		public AssetDescription?: string,
		public RelatedComments?: string
	) { }
}

/**
 * Asset Item
 */
export interface ILevelAsset {
	/** Asset ID */
	AssetID: number;
	/** Parent ID */
	ParentId: number
	/** Parent UID */
	ParentUID: string
	/** Asset Type */
	Type: number;
	/** Asset Name */
	Name: string;
	/** Asset Description */
	Description: string
	/** Asset in watch List */
	Watch: boolean
	/** Asset Expanded in structure*/
	Expanded?: boolean;
	/** Asset Item Image */
	ImageURL: string;
	/** Functional Level */
	FunctionalLocation: string;
	/** Equipment Code */
	EquipmentCode: string;
	/** How far left the asset is on the screen */
	LeftPosition: number;
	/** How far up the asset is on the screen */
	TopPosition: number;
	/** Whether the asset should be displayed */
	DisplayOnImage: boolean;
	/** Whether the asset is hidden */
	Hidden: boolean;
	/** Last Modified Epoch Time */
	Modified: number;
	/** Actioned */
	Actioned: boolean;
	/** AssetTileConfig */
	AssetTileConfig?: any;
	/** RuleCount */
	RuleCount: number;
	/** ModelCount */
	ModelCount: number;
	/** ParentFunctionalLocation */
	ParentFunctionalLocation: string;
	/** ParentOrder */
	ParentOrder: number;
}

/**
* Level Asset
* implements ILevelAsset
*/
export class LevelAsset implements ILevelAsset {
	/**
	 * AssetItem
	 * @param Uid UID
	 * @param AssetID AssetID
	 * @param [ParentId=0] 
	 * @param ParentUId ParentUId
	 * @param [ParentUID=""] 
	 * @param Type Type
	 * @param Name Name
	 * @param Description Description
	 * @param Watch Watch
	 * @param Expanded Expanded
	 * @param ImageURL ImageURL
	 * @param FunctionalLocation FunctionalLocation
	 * @param EquipmentCode EquipmentCode
	 * @param LeftPosition LeftPosition
	 * @param TopPosition TopPosition
	 * @param DisplayOnImage DisplayOnImage
	 * @param Hidden Hidden
	 * @param Modified Modified
	 * @param Actioned Actioned
	 * @param AssetTileConfig AssetTileConfig
	 * @param RuleCount RuleCount
	 * @param ModelCount ModelCount
	 * @param ParentFunctionalLocation ParentFunctionalLocation
	 * @param HierarchyPath 
	 * @param ParentOrder
	 */
	constructor(
		/** Unique ID  for Settings Page Only*/
		public Uid: any = "",
		/** Asset ID */
		public AssetID: number = 0,
		/** ID */
		public ParentId: number = 0,
		/** ParentUID */
		public ParentUID: string = "",
		/** Asset Type */
		public Type: number = 0,
		/** Asset Name */
		public Name: string = "",
		/** Asset Description */
		public Description: string = "",
		/** Asset in watch List */
		public Watch: boolean = false,
		/** Asset Expanded in structure*/
		public Expanded: boolean = false,
		/** Asset Item Image */
		public ImageURL: string = "",
		/** Asset Functional Location */
		public FunctionalLocation: string = "",
		/** Asset Equipment Code */
		public EquipmentCode: string = "",
		/** Asset Left Position */
		public LeftPosition: number = 0,
		/** Asset Top Position */
		public TopPosition: number = 0,
		/** Should asset be displayed on image */
		public DisplayOnImage: boolean = false,
		/** Is the asset hidden */
		public Hidden: boolean = false,
		/** Last Modified Epoch Time */
		public Modified: number = 0,
		/** Actioned */
		public Actioned: boolean = false,
		/** AssetTileConfig */
		public AssetTileConfig?: any,
		/** RuleCount
		 * #69 Assets Without Rules Page
		 * */
		public RuleCount: number = 0,
		/** ModelCount
		 * #69 Assets Without Rules Page
		 */
		public ModelCount: number = 0,

		public ParentFunctionalLocation: string = "",

		public ParentOrder: number = 0,

		/** Not part of the Class from Back End */
		public HierarchyPath?: string,

		/** Git#109 - Only returned on favourite asset page as part of JSON*/
		/** AccountGUID */
		public AccountGUID?: string,
		/** OrganisationUID */
		public OrganisationUID?: string,
		/** AccountName */
		public AccountName?: string
	) { }
}

/**
 * Class
 */
@Injectable({
	providedIn: 'root'
})
export class AssetsDataService {

	/** apiAssets */
	private apiAssets = "api/{0}/organisation/{1}/Assets";

	/**
   * Default Constructor
   * @param http Imports the 'HttpClient' Object for internet requests
   * @param globalFunctions Global Functions
   * @param siteSubscriptions site Subscriptions
   */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions,
		public siteSubscriptions: siteSubscriptions
	) { }


	/** getOrganisationAssets Cache */
	private getOrganisationAssets$Cache = new Map<string, CacheEntry<any>>();

	/**
	 * Get a list of Assets for an Organisations from API
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @returns Observable<any>
	 */
	getOrganisationAssets$(AccountGUID: string, orgUID: string = "", assetID: number = 0): Observable<any> {

		// const data = {};
		// let url = this.apiAssets;
		// url = url.replace("{0}", AccountGUID);
		// url = url.replace("{1}", orgUID);

		// url = `${environment.apiUrl}/${url}`;

		// if (assetID != 0) {
		// 	url = url + `/${assetID}`;
		// };
		// return this.globalFunctions.fetchAPI(this.http, url, "GET", data);

		const key = `${AccountGUID}|${orgUID}|${assetID}`;

		return this.globalFunctions.cacheRequest(this.getOrganisationAssets$Cache, key, () => {

			const data = {};
			let url = this.apiAssets;
			url = url.replace("{0}", AccountGUID);
			url = url.replace("{1}", orgUID);

			url = `${environment.apiUrl}/${url}`;

			if (assetID != 0) {
				url = url + `/${assetID}`;
			};
			return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
		});

	}

	/**
	 * Get a list of Assets for on the watch list for an organisation
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @returns Observable<any>
	 */
	getWatchListAssets$(AccountGUID: string, orgUID: string): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/Watched`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Get Data about an asset in the watch list
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @returns Observable<any>
	 */
	getWatchedAssetData$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/WatchedData/${assetID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Patch watch list data for an asset
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param watchListAssetID watchListAssetID  
	 * @param watchedData watchedData 
	 * @returns 
	 */
	patchWatchedAssetData$(AccountGUID: string, orgUID: string, watchListAssetID: number, watchedData: WatchedAssetData): Observable<any> {
		let data = JSON.stringify(watchedData);

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/WatchedData/${watchListAssetID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	  * Get a list of DataPoints for an Asset from API
	* @param AccountGUID
	* @param orgUID
	* @param dataPointID
	* @param assetID
	* @returns Observable<any>
	*/
	getAssetDataPoints$(AccountGUID: string, orgUID: string, assetID: number = 0, chartOnly: boolean = false,): Observable<any> {

		const data = {
			chartOnly: chartOnly
		};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/datapoints`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}


	/**
	 * Get Values for Datapoints from API
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param dateStart
	 * @param dateEnd
	 * @returns Observable<any>
	 */
	getAssetDataPointValues$(AccountGUID: string, orgUID: string, assetID: number, dateStart: number, dateEnd: number, chartOnly: boolean = false): Observable<any> {

		const data = {
			from: dateStart,
			to: dateEnd,
			chartOnly: chartOnly
		};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/datapointValues`.toLowerCase();
		// console.log(url)
		//url += `?from=${dateStart}&to=${dateEnd}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * getDataPointValues
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param dateStart 
	 * @param dateEnd 
	 * @param datapointIDs 
	 * @returns 
	 */
	getDataPointValues$(AccountGUID: string, orgUID: string, dateStart: number, dateEnd: number, datapointIDs: string, assetIDs?: string): Observable<any> {
		const data = {
			from: dateStart,
			to: dateEnd,
			chartOnly: false,
			dataPointIDs: datapointIDs,
			assetIDs: assetIDs ?? ''
		};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${0}/datapointValues`.toLowerCase();
		// console.log(url)
		//url += `?from=${dateStart}&to=${dateEnd}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Add (put) new Datapoint onto an Asset
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param dataPointID
	 * @returns Observable<any>
	 */
	putAssetDataPoint$(AccountGUID: string, orgUID: string, assetID: number, dataPointIDs: string): Observable<any> {

		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/datapoints`.toLowerCase();
		url += `?dataPointIDs=${dataPointIDs}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PUT", data);
	}

	/**
	 * Add (put) new Datapoint onto an Asset
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param dataPointID
	 * @returns Observable<any>
	 */
	patchAssetDataPoint$(AccountGUID: string, orgUID: string, assetID: number, dataPointID: number, assetDataPoint: AssetDataPoint): Observable<any> {
		let data = JSON.stringify(assetDataPoint);

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/datapoints`.toLowerCase();
		url += `?dataPointID=${dataPointID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * Delete Datapoint on an Asset
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param dataPointID
	 * @returns Observable<any>
	 */
	deleteAssetDataPoint$(AccountGUID: string, orgUID: string, assetID: number, dataPointID: number): Observable<any> {

		const data = {};
		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/datapoints`.toLowerCase();
		url += `?dataPointID=${dataPointID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * Post the Selected Asset Information
	 * @param AccountGUID
	 * @param orgUID
	 * @param asset
	 * @returns
	 */
	postOrganisationAssets$(AccountGUID: string, orgUID: string, subLocationID: string, asset: ILevelAsset): Observable<any> {
		// const data = {
		// 	"AssetID":asset.AssetID,
		// 	"Name": asset.Name,
		// 	"Description": asset.Description,
		// 	"ImageURL": asset.ImageURL,
		// 	"Watch": asset.Watch,
		// 	"Type": asset.Type,
		// 	"Expanded": asset.Expanded,
		// 	"ParentUId": asset.ParentUId
		// };
		let data = JSON.stringify(asset);

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/location/${subLocationID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Patch the Selected Asset Information
	 * @param AccountGUID
	 * @param orgUID
	 * @param asset
	 * @returns
	 */
	patchOrganisationAssets$(AccountGUID: string, orgUID: string, asset: any): Observable<any> {
		// const data = {
		// 	"AssetID":asset.AssetID,
		// 	"Name": asset.Name,
		// 	"Description": asset.Description,
		// 	"ImageURL": asset.ImageURL,
		// 	"Watch": asset.Watch,
		// 	"Type": asset.Type,
		// 	"Expanded": asset.Expanded,
		// 	"ParentUId": asset.ParentUId
		// };
		let data = JSON.stringify(asset);

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${asset.AssetID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	moveOrganisationAssets$(AccountGUID: string, orgUID: string, assetID: number, parentUID: string): Observable<any> {
		let data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/move?parentuid=${parentUID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);

	}

	/**
	 * Find Assets for the selected folder and children
	 * @param data
	 * @param watchList
	 * @returns
	 */
	//getAssets(targetUID: string, data: HierarchicalItem[]): { id: string; type: number; name: string; description: string }[] {
	findAssets(data: IOrganisationsHierarchy, watchList: boolean = false, actioned: boolean = false): any {

		let assetIds: LevelAsset[] = []

		function findAssetIdsRecursively(items: IOrganisationsHierarchy) {

			try {
				if (items.Assets) {
					// assetIds = assetIds.concat(items.Assets.map(asset => asset.assetID));
					// loop each child
					for (const asset of items.Assets) {
						if (!assetIds.find(({ AssetID }) => AssetID == asset.AssetID)) {
							if (!watchList && !actioned) {
								assetIds.push(asset);
							}
							else if (watchList && (asset.Watch == true)) {
								assetIds.push(asset);
							}
							else if (actioned && (asset.Actioned == true)) {
								assetIds.push(asset);
							}
							else if ((watchList && actioned) && (asset.Watch == true) && (asset.Actioned == true)) { //shouldn't happen
								assetIds.push(asset);
							}
						};
					};
				};
				// Check the node has any children
				if (items.SubChild) {
					// loop each child
					for (const item of items.SubChild) {
						// Noed the node have Assets
						if (item.Assets) {
							for (const asset of item.Assets) {
								if (!assetIds.find(({ AssetID }) => AssetID == asset.AssetID)) {
									if (!watchList && !actioned) {
										assetIds.push(asset)
									}
									else if (watchList && (asset.Watch == true)) {
										assetIds.push(asset)
									}
									else if (actioned && (asset.Actioned == true)) {
										assetIds.push(asset);
									}
									else if ((watchList && actioned) && (asset.Watch == true) && (asset.Actioned == true)) { //shouldn't happen
										assetIds.push(asset);
									}
								};
							};
							// assetIds = assetIds.concat(item.Assets.map(asset => asset.assetID));
						};
						if (item.SubChild) {
							findAssetIdsRecursively(item);
						};
					};
				};
			}
			catch (error) { console.error("Not an array of Items") };
		};
		findAssetIdsRecursively(data);
		return assetIds;
	}

	/**
	 * Delete an Asset
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @returns Observable<any>
	 */
	deleteAsset$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {

		const data = {};
		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}`.toLowerCase();

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}


	/**
	 * Get Location UID from Asset ID
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @returns 
	 */
	getLocationUIDfromAssetID$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {

		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/locationUID`.toLowerCase();
		// console.log(url)
		//url += `?from=${dateStart}&to=${dateEnd}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	  * Get a list of DataPoints for an Asset from API
	* @param AccountGUID
	* @param orgUID
	* @param dataPointID
	* @param assetID
	* @returns Observable<any>
	*/
	getUserFavouriteAssets$(AccountGUID: string, orgUID: string): Observable<any> {

		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/userFavourites`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	  * Get a list of DataPoints for an Asset from API
	* @param AccountGUID
	* @param orgUID
	* @param dataPointID
	* @param assetID
	* @returns Observable<any>
	*/
	postUserFavouriteAssets$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {

		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/userFavourites/${assetID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	  * Get a list of DataPoints for an Asset from API
	* @param AccountGUID
	* @param orgUID
	* @param dataPointID
	* @param assetID
	* @returns Observable<any>
	*/
	deleteUserFavouriteAssets$(AccountGUID: string, orgUID: string, assetID: number, userSub: string): Observable<any> {

		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/userFavourites/remove/${assetID}`;

		url += `?userSub=${userSub}`;

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * get flagged assets
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @returns 
	 */
	getFlaggedAssets$(AccountGUID: string, orgUID: string): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/flagged`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * post flagged asset
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @param comment comment 
	 * @returns 
	 */
	postFlaggedAsset$(AccountGUID: string, orgUID: string, assetID: number, comment: string): Observable<any> {
		const data = JSON.stringify(comment);

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/flagged/${assetID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * delete flagged asset
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @returns 
	 */
	deleteFlaggedAsset$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/flagged/remove/${assetID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * Get asset history
	 * @param AccountGUID AccountGUID   
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @returns 
	 */
	getAssetHistory$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/history`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Put Datapoints on multiple assets
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetIDs assetIDs 
	 * @param dataPointIDs dataPointIDs 
	 * @returns 
	 */
	putMultiAssetDataPoint$(AccountGUID: string, orgUID: string, assetIDs: string, dataPointIDs: string): Observable<any> {
		const data = {};

		let url = this.apiAssets;

		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);
		url = `${environment.apiUrl}/${url}/link`.toLowerCase();

		url += `?assetIDs=${assetIDs}&dataPointIDs=${dataPointIDs}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PUT", data);
	}

	/**
	 * patchAssetTileConfiguration
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @param backgroundColour backgroundColour  
	 * @param numberOfKPIs numberOfKPIs 
	 * @param kpiConfiguration kpiConfiguration 
	 * @param opacity opacity 
	 * @returns 
	 */
	patchAssetTileConfiguration$(AccountGUID: string, orgUID: string, assetID: number, backgroundColour: string,
		numberOfKPIs: number, kpiConfiguration: string, opacity: number): Observable<any> {

		let data = JSON.stringify({
			BackgroundColour: backgroundColour,
			NumberOfKPIs: numberOfKPIs,
			KPIConfiguration: kpiConfiguration,
			Opacity: opacity
		});

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/tile`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * deleteAssetTileConfiguration
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @returns 
	 */
	deleteAssetTileConfiguration$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/tile`;

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * getEnergyAssets
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @returns 
	 */
	getEnergyAssets$(AccountGUID: string, orgUID: string): Observable<any> {

		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/energyAssets`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * getAssetInformation
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @returns 
	 */
	getAssetInformation$(AccountGUID: string, orgUID: string, assetID: number): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/information`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * postAssetInformation
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @param assetInformation assetInformation  
	 * @returns 
	 */
	postAssetInformation$(AccountGUID: string, orgUID: string, assetID: number, assetInformation: AssetInformation, file?: FormData): Observable<any> {
		const data = assetInformation;

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/information`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * deleteAssetInformation
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID
	 * @param assetInformationGUID assetInformationGUID 
	 * @returns 
	 */
	deleteAssetInformation$(AccountGUID: string, orgUID: string, assetInformationGUID: string): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/information/${assetInformationGUID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * getAssetTypes
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @returns 
	 */
	getAssetTypes$(AccountGUID: string, orgUID: string): Observable<any> {
		const data = {};

		let url = this.apiAssets;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/assetTypes`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}
}
